import { mockResponse, request, isMockMode } from './client.js';
import { PRODUCTS } from '../data/products.js';

/**
 * 商品 API。
 *
 * 設計重點：
 *   - mock 階段用可變副本 _store，讓後台 CRUD 在 session 內生效
 *     （重新整理頁面會 reset，因為沒有持久化）
 *   - 真實後端串接後 _store 失去意義，所有讀寫都打 API
 */

let _store = [...PRODUCTS];

// 把參數物件轉成 query string，並濾掉 null / undefined / 空字串，
// 避免 URLSearchParams 把 null 變成字串 "null" 送給後端（會篩不到資料）
function toQuery(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') sp.append(k, v);
  });
  const qs = sp.toString();
  return qs ? '?' + qs : '';
}

// ────────────────────────────────────────────────────────────────────
// 前台讀取
// ────────────────────────────────────────────────────────────────────

// 前台商品列表（分頁）。回傳 PageResponse 形狀：{ items, total, page, limit, totalPages }
// 參數：categoryId / brand / search / sort / tag / featured / page / limit
// featured 走同一支：帶 limit 取前 N 筆即可（首頁精選用）。
export function getProducts(params = {}) {
  if (!isMockMode('products')) {
    return request(`/api/products${toQuery(params)}`);
  }
  const { featured, categoryId, brand, search, sort, tag, page = 1, limit = 12 } = params;
  return mockResponse(() => {
    let result = _store.filter(p => p.status === 'active');
    if (categoryId) result = result.filter(p => p.categoryId === categoryId);
    if (brand) result = result.filter(p => p.brand === brand);
    if (search) {
      const kw = search.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(kw) ||
        p.brand.toLowerCase().includes(kw) ||
        (p.description || '').toLowerCase().includes(kw)
      );
    }

    // featured 依銷量；其餘依 sort
    if (featured) {
      result = [...result].sort((a, b) => b.soldCount - a.soldCount);
    } else {
      switch (sort) {
        case 'price-asc':  result = [...result].sort((a, b) => a.price - b.price); break;
        case 'price-desc': result = [...result].sort((a, b) => b.price - a.price); break;
        case 'newest':     result = [...result].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')); break;
        case 'popular':    result = [...result].sort((a, b) => b.soldCount - a.soldCount); break;
        default: break;
      }
    }

    if (tag) result = result.filter(p => (p.tags || []).includes(tag));

    const total = result.length;
    const start = (page - 1) * limit;
    return {
      items: limit > 0 ? result.slice(start, start + limit) : result,
      total,
      page,
      limit,
      totalPages: limit > 0 ? Math.ceil(total / limit) : 0,
    };
  });
}

export function getProduct(id) {
  if (!isMockMode('products')) return request(`/api/products/${id}`);
  return mockResponse(() => _store.find(p => p.id === id) || null);
}

export function getRelatedProducts(id, limit = 4) {
  if (!isMockMode('products')) return request(`/api/products/${id}/related?limit=${limit}`);
  return mockResponse(() => {
    const target = _store.find(p => p.id === id);
    if (!target) return [];
    return _store
      .filter(p => p.id !== id && p.categoryId === target.categoryId && p.status === 'active')
      .slice(0, limit);
  });
}

// ────────────────────────────────────────────────────────────────────
// 後台 CRUD
// ────────────────────────────────────────────────────────────────────

export function adminGetProducts(params = {}) {
  if (!isMockMode('products')) {
    return request(`/api/admin/products${toQuery(params)}`);
  }
  return mockResponse(() => {
    const page = params.page || 1;
    const limit = params.limit || 10;
    let filtered = filterProducts(_store, { ...params, limit: 0 });
    // 後台排序：sort = "欄位,方向"（name/status/createdAt）
    const [sf, sd] = (params.sort || '').split(',');
    if (sf) {
      filtered = [...filtered].sort((a, b) => {
        const r = String(a[sf] ?? '').localeCompare(String(b[sf] ?? ''), undefined, { numeric: true });
        return sd === 'desc' ? -r : r;
      });
    }
    const start = (page - 1) * limit;
    return {
      items: filtered.slice(start, start + limit),
      total: filtered.length,
      page,
      limit,
      totalPages: Math.ceil(filtered.length / limit),
    };
  });
}

// 後台單一商品：含 draft / soldout（編輯頁載入用，與前台只回 active 的 getProduct 不同）
export function adminGetProduct(id) {
  if (!isMockMode('products')) return request(`/api/admin/products/${id}`);
  return mockResponse(() => _store.find(p => String(p.id) === String(id)) || null);
}

export function adminCreateProduct(data) {
  if (!isMockMode('products')) return request('/api/admin/products', { method: 'POST', body: data });
  return mockResponse(() => {
    const id = data.id || `p-${Date.now().toString(36)}`;
    const newProduct = {
      // 預設值，避免後台漏填關鍵欄位
      images: [],
      specs: {},
      variants: [],
      tags: [],
      rating: 0,
      reviewCount: 0,
      soldCount: 0,
      ...data,
      id,
      createdAt: new Date().toISOString().slice(0, 10),
    };
    _store = [..._store, newProduct];
    return newProduct;
  });
}

export function adminUpdateProduct(id, patch) {
  if (!isMockMode('products')) return request(`/api/admin/products/${id}`, { method: 'PATCH', body: patch });
  return mockResponse(() => {
    const idx = _store.findIndex(p => p.id === id);
    if (idx === -1) throw new Error(`找不到商品：${id}`);
    const updated = { ..._store[idx], ...patch };
    _store = [..._store.slice(0, idx), updated, ..._store.slice(idx + 1)];
    return updated;
  });
}

export function adminDeleteProduct(id) {
  if (!isMockMode('products')) return request(`/api/admin/products/${id}`, { method: 'DELETE' });
  return mockResponse(() => {
    _store = _store.filter(p => p.id !== id);
    return { ok: true };
  });
}

// ────────────────────────────────────────────────────────────────────
// 工具
// ────────────────────────────────────────────────────────────────────

function filterProducts(list, { featured, categoryId, brand, search, status, limit, sort, _publicOnly } = {}) {
  let result = [...list];

  if (_publicOnly) {
    // 前台只看上架商品
    result = result.filter(p => p.status === 'active');
  } else if (status && status !== 'all') {
    result = result.filter(p => p.status === status);
  }

  if (featured) {
    return result.sort((a, b) => b.soldCount - a.soldCount).slice(0, 4);
  }

  if (categoryId) result = result.filter(p => p.categoryId === categoryId);
  if (brand)      result = result.filter(p => p.brand === brand);
  if (search) {
    const kw = search.toLowerCase();
    result = result.filter(p =>
      p.name.toLowerCase().includes(kw) ||
      p.brand.toLowerCase().includes(kw) ||
      (p.description || '').toLowerCase().includes(kw)
    );
  }

  switch (sort) {
    case 'price-asc':  result = [...result].sort((a, b) => a.price - b.price); break;
    case 'price-desc': result = [...result].sort((a, b) => b.price - a.price); break;
    case 'newest':     result = [...result].sort((a, b) => (b.createdAt || '').localeCompare(a.createdAt || '')); break;
    case 'popular':    result = [...result].sort((a, b) => b.soldCount - a.soldCount); break;
    default: break;
  }

  if (limit) result = result.slice(0, limit);
  return result;
}
